/**
 * FormView - 表單檢視（簡化版）
 * 
 * 顯示已提交的表單，包括：
 * - 表單內容（只讀）
 * - 審批流程狀態
 * - 審批歷史
 * - 管理員特權面板
 */

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useFormDetail } from '../../../hooks/useApi';
import DynamicFormRenderer from '../../components/DynamicFormRenderer';
import AdminPanel from '../../components/AdminPanel';

const FormViewNew = () => {
    const { applicationId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, getText, language } = useI18n(['common', 'forms']);

    const { data: result, isLoading, error } = useFormDetail(applicationId);
    const form = result?.data;

    // 管理員權限檢查
    const isAdmin = user?.admin || user?.roles?.includes('admin');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !form) {
        return (
            <div className="p-6 text-center text-red-500">
                {error?.message || t('forms:view.formNotFound')}
            </div>
        );
    }

    const formConfig = form.config?.form;
    const approvalFlow = form.approvalFlow || [];
    const approvalHistory = form.approvalHistory || [];

    // 狀態顏色
    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'draft': return 'bg-gray-100 text-gray-800';
            case 'returned': return 'bg-orange-100 text-orange-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // 狀態文字
    const getStatusText = (status) => {
        return t(`forms:status.${status}`, status);
    };

    // 動作文字
    const getActionText = (action) => {
        return t(`forms:action.${action}`, action);
    };

    return (
        <div className="max-w-6xl mx-auto p-4 lg:p-6">
            {/* 頁面標題 */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {form.applicationId}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        {getText(formConfig?.formName) || form.formId}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(form.status)}`}>
                        {getStatusText(form.status)}
                    </span>
                    {/* Admin 編輯按鈕 */}
                    {isAdmin && (
                        <Link
                            to={`/forms/edit/${applicationId}`}
                            className="px-3 py-1 border border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50"
                        >
                            {t('forms:view.edit')}
                        </Link>
                    )}
                    <Link
                        to={`/forms/print/${applicationId}`}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                        {t('forms:view.print')}
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 主表單區域 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 申請人資訊 */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="font-medium text-gray-900 mb-3">{t('forms:view.applicantInfo')}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">{t('forms:view.name')}：</span>
                                <span className="font-medium">{form.applicant?.displayName}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">{t('forms:view.department')}：</span>
                                <span className="font-medium">{form.applicant?.department}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">{t('forms:view.jobTitle')}：</span>
                                <span className="font-medium">{form.applicant?.jobTitle}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">{t('forms:view.submitTime')}：</span>
                                <span className="font-medium">
                                    {form.submittedAt ? new Date(form.submittedAt).toLocaleString() : '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* 表單內容 */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="font-medium text-gray-900 mb-4">{t('forms:view.formContent')}</h3>
                        {formConfig ? (
                            <DynamicFormRenderer
                                formConfig={formConfig}
                                formData={form.formData || {}}
                                readOnly={true}
                                applicationId={applicationId}
                                formId={form.formId}
                            />
                        ) : (
                            <div className="text-sm text-gray-500">
                                {t('forms:view.cannotLoadConfig')}
                            </div>
                        )}
                    </div>

                    {/* 審批歷史 */}
                    {approvalHistory.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-medium text-gray-900 mb-3">{t('forms:view.approvalHistory')}</h3>
                            <div className="space-y-3">
                                {approvalHistory.map((record, idx) => (
                                    <div key={idx} className="flex items-start gap-3 text-sm">
                                        <div className="w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">
                                                    {record.actionBy?.displayName}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    record.action === 'approve' ? 'bg-green-100 text-green-700' :
                                                    record.action === 'reject' ? 'bg-red-100 text-red-700' :
                                                    record.action === 'return' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {getActionText(record.action)}
                                                </span>
                                            </div>
                                            <div className="text-gray-500">
                                                {getText(record.stepName)} • {new Date(record.timestamp).toLocaleString()}
                                            </div>
                                            {record.comment && (
                                                <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded">
                                                    {record.comment}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* 右側邊欄 */}
                <div className="lg:col-span-1 space-y-4">
                    {/* 管理員面板 */}
                    {isAdmin && (
                        <AdminPanel
                            applicationId={applicationId}
                            form={form}
                            onActionComplete={() => window.location.reload()}
                            getText={getText}
                        />
                    )}
                    
                    {/* 審批流程 */}
                    <div className="bg-white rounded-lg shadow p-4 sticky top-4">
                        <h3 className="font-medium text-gray-900 mb-3">{t('forms:view.approvalFlow')}</h3>
                        <div className="space-y-2">
                            {approvalFlow.map((step, idx) => {
                                const isSkipped = step.status === 'skipped' || step.isSkipped;
                                return (
                                <div 
                                    key={step.stepId || idx}
                                    className={`p-3 rounded text-sm ${
                                        isSkipped ? 'bg-gray-100 border-l-4 border-gray-200 opacity-60' :
                                        step.status === 'current' ? 'bg-blue-50 border-l-4 border-blue-500' :
                                        step.status === 'completed' ? 'bg-green-50 border-l-4 border-green-500' :
                                        step.status === 'rejected' ? 'bg-red-50 border-l-4 border-red-500' :
                                        'bg-gray-50 border-l-4 border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className={`font-medium ${isSkipped ? 'line-through text-gray-400' : ''}`}>
                                            {getText(step.stepName)}
                                        </span>
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                            isSkipped ? 'bg-gray-200 text-gray-500' :
                                            step.status === 'current' ? 'bg-blue-100 text-blue-700' :
                                            step.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            step.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                            'bg-gray-200 text-gray-600'
                                        }`}>
                                            {isSkipped ? t('forms:view.skipped') :
                                             step.status === 'current' ? t('forms:view.reviewing') :
                                             step.status === 'completed' ? t('forms:view.completed') :
                                             step.status === 'rejected' ? t('forms:status.rejected') :
                                             step.status === 'pending' ? t('forms:view.pendingApproval') : step.status}
                                        </span>
                                    </div>
                                    {step.approvers?.length > 0 && (
                                        <div className="mt-2 text-xs text-gray-600">
                                            {step.approvers.map((a, i) => (
                                                <div key={i} className="flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                                                    <span>{a.displayName}</span>
                                                    {a.role && <span className="text-gray-400">({a.role})</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {step.completedAt && (
                                        <div className="mt-1 text-xs text-gray-400">
                                            {new Date(step.completedAt).toLocaleString()}
                                        </div>
                                    )}
                                    {step.isOverridden && !isSkipped && (
                                        <div className="mt-1">
                                            <span className="text-xs bg-yellow-100 text-yellow-700 px-1 rounded">
                                                {t('forms:view.overridden')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )})}
                        </div>
                    </div>
                </div>
            </div>

            {/* 返回按鈕 */}
            <div className="mt-6">
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                    {t('forms:view.back')}
                </button>
            </div>
        </div>
    );
};

export default FormViewNew;
